/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { SidebarProvider, useSidebar } from "../context/SidebarContext";
import { Outlet, useNavigate } from "react-router";
import { useMemo, useState, useCallback, useEffect, ReactNode } from "react";
import AppHeader from "./AppHeader";
import Backdrop from "./Backdrop";
import AppSidebar from "./AppSidebar";
import ProtectedRoute from "../components/auth/ProtectedRoute";
import SubscriptionGuard from "../components/auth/SubscriptionGuard";
import { useAuth } from "../context/AuthContext";
import axios from "../lib/axios";
import Input from "../components/form/input/InputField";
import Label from "../components/form/Label";
import FloatingAskNetguruButton from "../components/ask-netguru/FloatingAskNetguruButton";
import AskNetguruDrawer from "../components/ask-netguru/AskNetguruDrawer";
import CredentialsModal from "../components/auth/CredentialsModal";
import PremiumChatWidget from "../components/ask-netguru/AskNetguruChat";
import ChatWidget from "../components/chatbot/ChatWidget";
import { MasterSyncProvider } from "../context/MasterSyncContext";
import SyncProgressBar from "../components/common/SyncProgressBar";
import Footer from "../components/footer/Footer";
import { CountdownModal } from "../pages/DeleteandDeactive/DeleteandDeactive";
import { useUserActivity } from "../hooks/useUserActivity";

type ShopifyScopeStatus = {
  requires_update: boolean;
  shop?: string;
  missing_scopes?: string[];
  reauthorize_url?: string | null;
};

const LayoutContent: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const { isExpanded, isHovered, isMobileOpen } = useSidebar();
  const {
    stores,
    refreshStores,
    currentSubscription,
    currentShop,
    loadSubscriptionData,
    user,
    checkAuth,
  } = useAuth();

  // Use user activity tracking
  useUserActivity();
  const navigate = useNavigate();
  const [installing, setInstalling] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [isAskNetguruOpen, setIsAskNetguruOpen] = useState(false);
  const [storeName, setStoreName] = useState("");
  const [storeError, setStoreError] = useState("");
  const [validating, setValidating] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showCountdownModal, setShowCountdownModal] = useState(false);
  const [scopeStatus, setScopeStatus] = useState<ShopifyScopeStatus | null>(null);
  const [checkingScopes, setCheckingScopes] = useState(false);
  const [updatingScopes, setUpdatingScopes] = useState(false);
  const pendingStores = useMemo(
    () => stores.filter((store) => !store.connected),
    [stores],
  );

  const connectedStores = useMemo(
    () => stores.filter((store) => store.connected),
    [stores],
  );

  // Only show install banner if there are no connected stores
  const needsInstall = connectedStores.length === 0;

  // For display, prefer showing connected stores, but for installation banner, show pending stores
  const highlightedStore = pendingStores[0] ?? stores[0] ?? null;
  const highlightedStoreLabel = highlightedStore
    ? highlightedStore.shop.replace(".myshopify.com", "")
    : null;

  // Get platform type from highlighted store
  const platformType = highlightedStore?.platform_type || 'shopify';

  // Get the current connected store for display purposes
  const currentConnectedStore = useMemo(() => {
    return (
      connectedStores.find(
        (store) =>
          store.shop === stores.find((s) => s.shop === store.shop)?.shop,
      ) ??
      connectedStores[0] ??
      null
    );
  }, [connectedStores, stores]);

  const normalizeShop = (value: string) => {
    let cleaned = value.trim().toLowerCase();
    if (!cleaned) return "";
    cleaned = cleaned.replace(/^https?:\/\//, "").replace(/\/$/, "");
    if (!cleaned.endsWith(".myshopify.com")) {
      cleaned = cleaned.includes(".") ? cleaned : `${cleaned}.myshopify.com`;
    }
    return cleaned;
  };

  const handleConnectStoreClick = useCallback(() => {
    if (highlightedStore) {
      setStoreName(highlightedStore.shop.replace(".myshopify.com", ""));
    } else {
      setStoreName("");
    }
    setStoreError("");
    setShowConnectModal(true);
  }, [highlightedStore]);

  // Listen for custom event to open connect store modal from dashboard
  useEffect(() => {
    const handleOpenModal = () => {
      setStoreName("");
      setStoreError("");
      setShowConnectModal(true);
    };

    window.addEventListener("openConnectStoreModal", handleOpenModal);
    return () => {
      window.removeEventListener("openConnectStoreModal", handleOpenModal);
    };
  }, []);

  // Load subscription data when component mounts or currentShop changes
  useEffect(() => {
    if (currentShop) {
      loadSubscriptionData(currentShop);
    }
  }, [currentShop]);

  useEffect(() => {
    if (!user || !currentShop || connectedStores.length === 0) {
      setScopeStatus(null);
      return;
    }

    let isMounted = true;
    setCheckingScopes(true);

    axios
      .get<ShopifyScopeStatus>("/shopify/check-scopes", {
        params: { shop: currentShop },
      })
      .then((response) => {
        if (isMounted) {
          setScopeStatus(response.data);
        }
      })
      .catch((error) => {
        console.error("Failed to check Shopify scope requirements", error);
        if (isMounted) {
          setScopeStatus(null);
        }
      })
      .finally(() => {
        if (isMounted) {
          setCheckingScopes(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [user, currentShop, connectedStores.length]);

  const handleUpdatePermissions = useCallback(() => {
    if (!scopeStatus?.reauthorize_url) {
      return;
    }

    setUpdatingScopes(true);
    window.location.href = scopeStatus.reauthorize_url;
  }, [scopeStatus?.reauthorize_url]);

  // Show password setup modal if user needs to set password
  useEffect(() => {
    if (user?.password_reset_required && !showPasswordModal) {
      setShowPasswordModal(true);
    }
  }, [user?.password_reset_required, showPasswordModal]);

  const handlePasswordModalClose = async () => {
    setShowPasswordModal(false);
    // Refresh auth to check if password was set
    await checkAuth();
  };
 
   
  // Global Shopify uninstall detection
  useEffect(() => {
    if (user?.pending_delete && user?.uninstalled) {
      setShowCountdownModal(true);
    }
  }, [user]);

  // Handle countdown completion - finalize delete and logout
  const handleCountdownComplete = async () => {
    try {
      await axios.post("/finalize-delete");
    } catch (error) {
      console.error("Failed to finalize delete:", error);
    } finally {
      // Safe logout
      if (typeof window !== 'undefined') {
        localStorage.setItem('is_logging_out', 'true');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('shop');
        localStorage.removeItem('stores');
        localStorage.removeItem('activeStoreId');
        sessionStorage.clear();
        localStorage.removeItem('auth_event');
        localStorage.setItem(
          'auth_event',
          JSON.stringify({
            type: 'logout',
            at: Date.now(),
          })
        );
        setTimeout(() => {
          localStorage.removeItem('is_logging_out');
          window.location.href = '/signin';
        }, 200);
      }
    }
  };

  const handleConnectStore = useCallback(async () => {
    if (installing || validating) {
      return;
    }

    if (!storeName.trim()) {
      setStoreError("Please enter your Shopify store URL.");
      return;
    }

    setStoreError("");
    setValidating(true);

    try {
      const normalizedShop = normalizeShop(storeName);
      if (!normalizedShop) {
        setStoreError("Please enter a valid Shopify store URL.");
        setValidating(false);
        return;
      }

      // Validate shop exists
      const validateRes = await axios.post("/validate-shop", {
        shop: normalizedShop,
      });
      if (!validateRes.data.exists) {
        setStoreError(
          "That Shopify store could not be found. Please check the domain.",
        );
        setValidating(false);
        return;
      }

      setInstalling(true);
      const response = await axios.post("/register-shop", {
        shop: normalizedShop,
      });

      const installUrl = response.data?.install_url;
      if (typeof installUrl === "string" && installUrl.length > 0) {
        // Close modal and refresh stores before redirecting
        setShowConnectModal(false);
        await refreshStores();
        window.location.href = installUrl;
        return;
      }

      setStoreError("Failed to retrieve install URL. Please try again.");
      setInstalling(false);
      setValidating(false);
    } catch (error: any) {
      console.error("Failed to initiate Shopify install", error);
      setStoreError(
        error.response?.data?.message ||
        "Failed to initiate shop installation. Please try again.",
      );
      setInstalling(false);
      setValidating(false);
    }
  }, [storeName, installing, validating, refreshStores]);

  return (
    <div className="min-h-screen">
      <div>
        <AppSidebar disableNavigation={needsInstall} />
        <Backdrop />
      </div>
      <div
        className={`min-h-screen flex flex-col transition-all duration-300 ease-in-out ${isExpanded || isHovered ? "lg:ml-[270px]" : "lg:ml-[90px]"
          } ${isMobileOpen ? "ml-0" : ""}`}
      >
        <AppHeader />
        <SyncProgressBar />
        {/* max-w-dashboard overflow-x-hidden*/}
        <div className="mx-auto w-full py-2.5 flex-1">
          {scopeStatus?.requires_update && scopeStatus.reauthorize_url && (
            <div className="mb-3 rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-teal-800 shadow-sm dark:border-teal-700 dark:bg-teal-900/20 dark:text-teal-100 mx-6">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-teal-600 dark:text-teal-300">
                    Shopify permissions update required
                  </p>
                  <p className="mt-0.5 text-sm text-gray-900 dark:text-white">
                    New permissions are required to keep your Shopify data syncing correctly.
                  </p>
                  {/* {scopeStatus.missing_scopes && scopeStatus.missing_scopes.length > 0 && (
                    <p className="mt-0.5 text-xs text-teal-700 dark:text-teal-200">
                      Missing scopes: {scopeStatus.missing_scopes.join(", ")}
                    </p>
                  )} */}
                </div>
                <button
                  type="button"
                  onClick={handleUpdatePermissions}
                  disabled={checkingScopes || updatingScopes}
                  className="inline-flex items-center justify-center rounded-md bg-teal-600 px-3 py-1.5 text-sm font-medium text-white shadow hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70 dark:hover:bg-teal-500 dark:focus:ring-offset-gray-900 transition-colors"
                >
                  {updatingScopes ? "Redirecting..." : "Update Permissions"}
                </button>
              </div>
            </div>
          )}

          {needsInstall && (
            <div className="mb-6 rounded-xl border border-teal-200 bg-teal-50 p-4 text-teal-800 shadow-sm dark:border-teal-700 dark:bg-teal-900/20 dark:text-teal-100 mx-6">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-red-400 dark:text-red-400">
                    Installation incomplete
                  </p>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">
                    Connect your store to get started with Netsights. You can add both Shopify and WooCommerce stores.
                  </p>
                  {highlightedStore && (
                    <span className="text-xs text-teal-400 dark:text-teal-400">
                      Pending store: {highlightedStore.shop} ({platformType === 'woocommerce' ? 'WooCommerce' : 'Shopify'})
                    </span>
                  )}
                </div>
                <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
                  <button
                    type="button"
                    onClick={handleConnectStoreClick}
                    disabled={installing}
                    className="inline-flex items-center justify-center rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed dark:hover:bg-teal-500 dark:focus:ring-offset-gray-900 transition-colors"
                  >
                    Connect Shopify
                  </button>
                  <hr className="w-full border-t border-teal-200 dark:border-teal-700 sm:hidden" />
                  <h1>OR</h1>
                 <hr className="w-full border-t border-teal-200 dark:border-teal-700 sm:hidden" />
                  <button
                    type="button"
                    onClick={() => navigate('/on-boarding?platform=woocommerce')}
                    disabled={installing}
                    className="inline-flex items-center justify-center rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed dark:hover:bg-teal-500 dark:focus:ring-offset-gray-900 transition-colors"
                  >
                    Connect WooCommerce
                  </button>
                </div>
              </div>
            </div>
          )}

          {children ?? <Outlet />}
        </div>
        <Footer />
      </div>

      {/* <PremiumChatWidget /> */}
      <ChatWidget />

      {/* Connect Store Modal */}
      {showConnectModal && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-lg dark:bg-gray-900">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
              Connect Shopify Store
            </h2>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
              Enter your Shopify store URL to connect and install the app.
            </p>
            {storeError && (
              <div className="mb-4 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
                {storeError}
              </div>
            )}
            <div className="mb-4">
              <Label>
                Shopify Store URL<span className="text-error-500">*</span>
              </Label>
              <Input
                type="text"
                placeholder="merchant.myshopify.com or store name"
                value={storeName}
                onChange={(e) => {
                  setStoreName(e.target.value);
                  setStoreError("");
                }}
                disabled={installing || validating}
                className="w-full"
              />
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowConnectModal(false);
                  setStoreName("");
                  setStoreError("");
                }}
                disabled={installing || validating}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-950 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConnectStore}
                disabled={installing || validating || !storeName.trim()}
                className={`flex items-center space-x-2 px-4 py-2 text-white bg-primary rounded-lg transition-colors ${
                  installing || validating || !storeName.trim()
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-primary/90"
                }`}
              >
                {installing
                  ? "Redirecting..."
                  : validating
                    ? "Validating..."
                    : "Connect & Install"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Setup Modal - shown when password_reset_required is true */}
      {showPasswordModal && (
        <CredentialsModal
          onClose={handlePasswordModalClose}
          passwordOnly={true}
        />
      )}

      <CountdownModal
        open={showCountdownModal}
        message="Your account has been deleted"
        onComplete={handleCountdownComplete}
      />
    </div>
  );
};

export const AppLayoutContainer: React.FC<{ children?: ReactNode }> = ({
  children,
}) => (
  <SidebarProvider>
    <MasterSyncProvider>
      <LayoutContent>{children}</LayoutContent>
    </MasterSyncProvider>
  </SidebarProvider>
);

const AppLayout: React.FC = () => {
  return (
    <ProtectedRoute>
      <SubscriptionGuard>
        <AppLayoutContainer />
      </SubscriptionGuard>
    </ProtectedRoute>
  );
};

export default AppLayout;
